import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useSocket } from '../../context/SocketContext';
import PaymentForm from '../../components/PaymentForm/PaymentForm';
import CustomSpinner from '../CustomSpinner/CustomSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChair, 
  faFilter, 
  faClock,
  faCircleCheck,
  faLock,
  faArrowRight,
  faUserGroup
} from '@fortawesome/free-solid-svg-icons';
import './TableComponent.css';
import { buildApiUrl } from '../../config/api.config';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_CLIENT_KEY);

const TAG_COLORS = {
  'Indoor': '#3498db',
  'Outdoor': '#27ae60',
  'Private': '#9b59b6',
  'Bar': '#e67e22',
  'Window': '#1abc9c',
  'VIP': '#f1c40f',
  'Quiet Zone': '#95a5a6'
};

const TableComponent = ({ showAlert }) => {
  const [tables, setTables] = useState([]);
  const [userId, setUserId] = useState('');
  const [loadingTable, setLoadingTable] = useState(null);
  const [capacityFilter, setCapacityFilter] = useState('');
  const [slotFilter, setSlotFilter] = useState('1');
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [selectedTableNumber, setSelectedTableNumber] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const socket = useSocket();

  useEffect(() => {
    fetchUserDetails();
    fetchTables();
    
    if (socket) {
      socket.emit('joinRoom', `slot_${slotFilter}`);
      socket.on('slotUpdated', handleSlotUpdate);
      socket.on('tableAdded', handleTableAdded);
      socket.on('tableDeleted', handleTableDeleted);
      socket.on('tableStatusChanged', handleTableStatusChanged);
      
      return () => {
        socket.off('slotUpdated', handleSlotUpdate);
        socket.off('tableAdded', handleTableAdded);
        socket.off('tableDeleted', handleTableDeleted);
        socket.off('tableStatusChanged', handleTableStatusChanged);
        socket.emit('leaveRoom', `slot_${slotFilter}`);
      };
    }
  }, [socket, slotFilter]);

  const handleTableStatusChanged = (data) => {
    if (data.slotNumber.toString() === slotFilter) {
      setTables(prevTables => {
        if (data.disabled) {
          return prevTables.filter(table => table.number !== data.tableNumber);
        } else {
          const exists = prevTables.some(table => table.number === data.tableNumber);
          if (!exists) {
            fetchSingleTable(data.tableNumber);
          }
          return prevTables;
        }
      });
    }
  };

  const fetchSingleTable = async (tableNumber) => {
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = (user?.role === 'owner' || user?.role === 'user') ? selectedRestId : user?.restaurantId;
      if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;
      
      const restParam = activeRestaurantId ? `?restaurantId=${activeRestaurantId}` : '';
      const response = await axios.get(buildApiUrl(`slot/${slotFilter}${restParam}`));
      const table = response.data.find(t => t.number === tableNumber);
      if (table && !table.disabled) {
        setTables(prevTables => {
          const exists = prevTables.some(t => t.number === tableNumber);
          if (!exists) return [...prevTables, table].sort((a, b) => a.number - b.number);
          return prevTables;
        });
      }
    } catch (error) {
      console.error('Error fetching single table:', error);
    }
  };

  const handleSlotUpdate = (data) => {
    if (data.slotNumber.toString() === slotFilter) {
      setTables(prevTables => {
        if (data.slot?.disabled) return prevTables.filter(table => table.number !== data.tableNumber);
        return prevTables.map(table => {
          if (table.number === data.tableNumber) {
            return {
              ...table,
              reserved: data.action === 'reserved',
              reservedBy: data.reservedBy || null
            };
          }
          return table;
        });
      });
    }
  };

  const handleTableAdded = (data) => {
    if (data.slotNumber.toString() === slotFilter && !data.table.disabled) {
      setTables(prevTables => [...prevTables, data.table].sort((a, b) => a.number - b.number));
    }
  };
  
  const handleTableDeleted = (data) => {
    if (data.slotNumber.toString() === slotFilter) {
      setTables(prevTables => prevTables.filter(table => table.number !== data.tableNumber));
    }
  };

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.post(buildApiUrl('users/getuser'), {}, { headers: { 'auth-token': token } });
      setUserId(response.data._id);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const fetchTables = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = (user?.role === 'owner' || user?.role === 'user') ? selectedRestId : user?.restaurantId;
      if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;
      const restParam = activeRestaurantId ? `?restaurantId=${activeRestaurantId}` : '';
      const response = await axios.get(buildApiUrl(`slot/${slotFilter}${restParam}`));
      setTables(response.data.filter(table => !table.disabled));
    } catch (error) {
      console.error('Error fetching tables:', error);
      showAlert('Error fetching tables', 'danger');
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    showAlert('Payment successful!', 'success');
    setPaymentIntent(paymentIntent);
    setShowPaymentForm(false);

    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = (user?.role === 'owner' || user?.role === 'user') ? selectedRestId : user?.restaurantId;
      if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;

      await axios.post(
        buildApiUrl(`slot/${selectedSlot}/reserve`),
        { 
          number: selectedTableNumber, 
          paymentIntentId: paymentIntent.id,
          restaurantId: activeRestaurantId
        },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      showAlert('Table reserved successfully', 'success');
    } catch (error) {
      console.error('Error reserving table:', error);
      showAlert('Error reserving table', 'danger');
    }
  };

  const handlePaymentError = (error) => {
    showAlert(`Payment failed: ${error}`, 'danger');
    setShowPaymentForm(false);
  };

  const toggleReservation = async (number, isReserved, reservedBy) => {
    if (isReserved && reservedBy !== userId) {
      showAlert('Table is already reserved by someone else', 'danger');
      return;
    }
  
    setLoadingTable(number);
    setSelectedSlot(slotFilter);
  
    if (!isReserved) {
      setSelectedTableNumber(number);
      const token = localStorage.getItem('token');
      if (!token) return;
  
      try {
        const { data } = await axios.post(
          buildApiUrl(`slot/${slotFilter}/create-payment-intent`),
          { amount: 100 },
          { headers: { 'auth-token': token } }
        );
        setPaymentIntent({ clientSecret: data.clientSecret, tableNumber: number });
        setShowPaymentForm(true);
      } catch (error) {
        console.error('Error creating payment intent:', error);
        showAlert('Error creating payment intent', 'danger');
      } finally {
        setLoadingTable(null);
      }
    } else {
      try {
        const user = JSON.parse(localStorage.getItem("userDetails"));
        const selectedRestId = localStorage.getItem("currentRestaurantId");
        let activeRestaurantId = (user?.role === 'owner' || user?.role === 'user') ? selectedRestId : user?.restaurantId;
        if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;

        await axios.post(
          buildApiUrl(`slot/${slotFilter}/unreserve`),
          { number, restaurantId: activeRestaurantId },
          { headers: { 'auth-token': localStorage.getItem('token') } }
        );
        showAlert('Table unreserved successfully', 'success');
      } catch (error) {
        console.error('Error unreserving table:', error);
        showAlert('Error unreserving table', 'danger');
      } finally {
        setLoadingTable(null);
      }
    }
  };

  const filteredTables = [...tables].sort((a, b) => a.number - b.number).filter(table => 
    (!capacityFilter || table.capacity === parseInt(capacityFilter))
  );

  const SLOTS = [
    { id: '1', label: '5:00 - 7:00' },
    { id: '2', label: '7:00 - 9:00' },
    { id: '3', label: '9:00 - 11:00' }
  ];

  return (
    <div className="tc-page-container">
      <div className="tc-ambient-bg"></div>
      
      <main className="tc-content">
        <header className="tc-header">
          <span className="tc-badge">Gourmet Reservations</span>
          <h1 className="tc-title">Choose Your <span>Experience</span></h1>
          <p className="tc-subtitle">Select the perfect table for your dining journey. Real-time availability from our premium floor map.</p>
        </header>

        <section className="tc-controls">
          <div className="tc-filter-group">
            <div className="tc-filter-item">
              <span className="tc-label">Select Timing Slot</span>
              <div className="tc-slot-pills">
                {SLOTS.map(slot => (
                  <button 
                    key={slot.id}
                    className={`tc-slot-pill ${slotFilter === slot.id ? 'active' : ''}`}
                    onClick={() => setSlotFilter(slot.id)}
                  >
                    <FontAwesomeIcon icon={faClock} style={{ marginRight: '8px' }} />
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="tc-filter-item">
              <span className="tc-label">Table Capacity</span>
              <div className="tc-select-wrap">
                <select 
                  className="tc-select"
                  value={capacityFilter}
                  onChange={(e) => setCapacityFilter(e.target.value)}
                >
                  <option value="">All Capacities</option>
                  <option value="2">2 Persons</option>
                  <option value="4">4 Persons</option>
                  <option value="6">6 Persons</option>
                  <option value="8">8 Persons</option>
                </select>
              </div>
            </div>
          </div>

          <div className="tc-legend">
            <div className="tc-legend-item">
              <div className="tc-dot available"></div>
              Available
            </div>
            <div className="tc-legend-item">
              <div className="tc-dot reserved"></div>
              Reserved
            </div>
            <div className="tc-legend-item">
              <div className="tc-dot yours"></div>
              Your Booking
            </div>
          </div>
        </section>

        <div className="tc-grid">
          {filteredTables.map((table) => {
            const isReservedByMe = table.reserved && table.reservedBy?._id === userId;
            const isReservedByOther = table.reserved && table.reservedBy?._id !== userId;
            
            return (
              <div 
                key={table.number} 
                className={`tc-card ${isReservedByMe ? 'yours' : ''} ${isReservedByOther ? 'reserved' : ''}`}
                onClick={() => !isReservedByOther && toggleReservation(table.number, table.reserved, table.reservedBy?._id)}
              >
                <div className="tc-card-icon">
                  <FontAwesomeIcon icon={faChair} />
                </div>

                <div className="tc-card-capacity">
                  <FontAwesomeIcon icon={faUserGroup} />
                  {table.capacity} Seats
                </div>

                <h3 className="tc-card-number">Table {table.number}</h3>

                {table.tags && table.tags.length > 0 && (
                  <div className="tc-tags">
                    {table.tags.map(tag => (
                      <span 
                        key={tag} 
                        className="tc-tag" 
                        style={{ backgroundColor: TAG_COLORS[tag] || '#95a5a6' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <button 
                  className={`tc-btn ${isReservedByMe ? 'tc-btn-unreserve' : isReservedByOther ? 'tc-btn-reserved' : 'tc-btn-select'}`}
                  disabled={loadingTable === table.number || isReservedByOther}
                >
                  {loadingTable === table.number ? (
                    <CustomSpinner />
                  ) : isReservedByMe ? (
                    <>
                      <FontAwesomeIcon icon={faCircleCheck} />
                      Unreserve
                    </>
                  ) : isReservedByOther ? (
                    <>
                      <FontAwesomeIcon icon={faLock} />
                      Occupied
                    </>
                  ) : (
                    <>
                      Book Now
                      <i className="fa-solid fa-arrow-right"></i>
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {showPaymentForm && (
          <div className="tf-modal-overlay" style={{ 
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(15px)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{ width: '100%', maxWidth: '800px', padding: '1rem' }}>
              <Elements stripe={stripePromise}>
                <PaymentForm
                  clientSecret={paymentIntent?.clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  tableNumber={selectedTableNumber}
                  slot={selectedSlot}
                  amount={100}
                  onClose={() => setShowPaymentForm(false)}
                />
              </Elements>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TableComponent;
